// hooks/useUniversalPreview.ts
import React, { useCallback } from 'react';
import { previewEngine, PreviewData } from '../services/previewEngine';
import { toast } from 'react-hot-toast';
import { usePreviewStore } from '../stores/previewStore';

export const useUniversalPreview = () => {
  const { isOpen, data, options, isLoading, setOpen, setData, setOptions, setLoading } = usePreviewStore();

  const preview = useCallback((config: {
    title: string;
    data: any;
    companyName?: string;
    year?: number;
    pageTitle?: string;
    moduleName?: string;
    columns?: Array<{
      key: string;
      title: string;
      width?: string;
      render?: (value: any) => string;
    }>;
    chartData?: any[];
    chartConfig?: {
      xAxisLabel: string;
      yAxisLabel: string;
      thresholds: {
        etki_hayati: number;
        etki_onemsiz: number;
        finans_hayati: number;
        finans_onemsiz: number;
      };
    };
  }) => {
    const rawData: PreviewData = {
      title: config.title,
      content: config.data,
      type: 'auto',
      format: 'auto',
      companyName: config.companyName,
      year: config.year,
      pageTitle: config.pageTitle,
      moduleName: config.moduleName,
      columns: config.columns,
      chartData: config.chartData,
      chartConfig: config.chartConfig
    };

    const { processedData, options } = previewEngine.processData(rawData);
    
    setData(processedData);
    setOptions(options);
    setOpen(true);
    setLoading(false);
  }, [setData, setOptions, setOpen, setLoading]);

  const close = useCallback(() => {
    setOpen(false);
    setData(null);
  }, [setOpen, setData]);

  const updateOptions = useCallback((newOptions: Partial<typeof options>) => {
    const currentOptions = usePreviewStore.getState().options;
    setOptions({ ...currentOptions, ...newOptions });
  }, [setOptions]);

  const downloadPDF = useCallback(async () => {
    if (!data) return;
    
    setLoading(true);
    const toastId = toast.loading('PDF oluşturuluyor...');
    
    try {
      // Lazy load paketleri
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      const previewElements = document.querySelectorAll('.preview-content');
      if (previewElements.length === 0) {
        throw new Error('Önizleme içeriği bulunamadı');
      }

      const pdf = new jsPDF({
        orientation: options?.format === 'landscape' ? 'l' : 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      for (let i = 0; i < previewElements.length; i++) {
        const element = previewElements[i] as HTMLElement;
        
        const canvas = await html2canvas(element, {
          scale: 1.5,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.75);
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      }

      const fileName = `${data.companyName || 'Rapor'}_${data.pageTitle || 'Analiz'}_${data.year || new Date().getFullYear()}.pdf`;
      pdf.save(fileName);

      toast.success('PDF indirildi!', { id: toastId });
    } catch (error: any) {
      console.error('PDF error:', error);
      toast.error('PDF oluşturulamadı: ' + error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  }, [data, options, setLoading]);

  const print = useCallback(() => {
    window.print();
  }, []);

  const downloadExcel = useCallback(async () => {
    if (!data) return;
    
    setLoading(true);
    const toastId = toast.loading('Excel dosyası oluşturuluyor...');
    
    try {
      // Lazy load xlsx paketi
      const XLSX = await import('xlsx');
      
      // Veriyi düzenle
      let excelData: any[] = [];
      
      // Kategori bazlı veri (GRI Genel gibi)
      if (Array.isArray(data.content) && data.content.length > 0 && data.content[0]?.category) {
        data.content.forEach((categoryData: any) => {
          // Kategori başlığı
          excelData.push({
            'No': '',
            'Referans': categoryData.category,
            'Soru': '',
            'Cevap': ''
          });
          
          // Kategori içindeki sorular
          const items = categoryData.items || [];
          items.forEach((item: any) => {
            excelData.push({
              'No': item.displayNo || '',
              'Referans': item.soruReferans || '',
              'Soru': item.soruMadde || '',
              'Cevap': item.cevap || '-'
            });
          });
          
          // Boş satır ekle
          excelData.push({});
        });
      }
      // Normal tablo verisi
      else if (Array.isArray(data.content)) {
        data.content.forEach((item: any) => {
          // Konu başlığı mı?
          if (item.konuAdi && !item['Soru No']) {
            excelData.push({
              'Konu': item.konuAdi
            });
          } else {
            excelData.push(item);
          }
        });
      }
      
      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Kolon genişliklerini ayarla
      ws['!cols'] = [
        { wch: 5 },   // No
        { wch: 15 },  // Referans
        { wch: 50 },  // Soru
        { wch: 50 }   // Cevap
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, data.pageTitle || 'Veri');
      
      // Dosya adı
      const fileName = `${data.companyName || 'Rapor'}_${data.pageTitle || 'Analiz'}_${data.year || new Date().getFullYear()}.xlsx`;
      
      // İndir
      XLSX.writeFile(wb, fileName);
      
      toast.success('Excel dosyası indirildi!', { id: toastId });
    } catch (error: any) {
      console.error('Excel error:', error);
      toast.error('Excel oluşturulamadı: ' + error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  }, [data, setLoading]);

  const openPreview = useCallback((config: {
    title: string;
    data: any;
    companyName?: string;
    year?: number;
    pageTitle?: string;
    columns?: Array<{
      key: string;
      title: string;
      width?: string;
      render?: (value: any) => string;
    }>;
    renderContent?: (data: any) => React.ReactNode;
  }) => {
    const rawData: PreviewData = {
      title: config.title,
      content: config.data,
      type: 'table',
      format: 'auto',
      companyName: config.companyName,
      year: config.year,
      pageTitle: config.pageTitle,
      columns: config.columns
    };

    const { processedData, options } = previewEngine.processData(rawData);
    
    setData(processedData);
    setOptions(options);
    setOpen(true);
    setLoading(false);
  }, [setData, setOptions, setOpen, setLoading]);

  return {
    isOpen,
    data,
    options,
    isLoading,
    preview,
    openPreview,
    close,
    updateOptions,
    downloadPDF,
    downloadExcel,
    print
  };
};
